import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Card,
  Stack,
  Tabs,
  Tab,
  InputAdornment,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
} from '@mui/material';
import { 
  Save, 
  Refresh,
  Settings as SettingsIcon,
  AssignmentReturn,
  Info,
  AttachMoney,
  Visibility,
  Edit,
  List as ListIcon,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useGetProductTaxQuery,
  useSetProductTaxMutation,
  useGetProductReturnLimitQuery,
  useSetProductReturnLimitMutation,
  useListAllProductTaxesQuery,
} from '../../features/admin/globalSettingsApi.ts';

const GlobalSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // Tax states
  const [taxName, setTaxName] = useState('');
  const [taxPercentage, setTaxPercentage] = useState('');
  const [currency, setCurrency] = useState('AED');
  
  // Return limit states
  const [returnLimit, setReturnLimit] = useState('');
  
  // Product tax list states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Common states
  const [hasTaxChanges, setHasTaxChanges] = useState(false);
  const [hasReturnLimitChanges, setHasReturnLimitChanges] = useState(false);
  
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // API hooks
  const { 
    data: taxData, 
    isLoading: isTaxLoading, 
    error: taxError, 
    isError: isTaxError, 
    refetch: refetchTax 
  } = useGetProductTaxQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [setProductTax, { isLoading: isTaxSaving }] = useSetProductTaxMutation();

  const { 
    data: limitData, 
    isLoading: isLimitLoading, 
    error: limitError, 
    isError: isLimitError, 
    refetch: refetchLimit 
  } = useGetProductReturnLimitQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [setProductReturnLimit, { isLoading: isLimitSaving }] = useSetProductReturnLimitMutation();

  // Product tax list hook
  const { 
    data: productTaxListData, 
    isLoading: isListLoading, 
    error: listError, 
    isError: isListError, 
    refetch: refetchList 
  } = useListAllProductTaxesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  // Load initial tax data
  useEffect(() => {
    console.log('=== taxData received ===', taxData);
    
    if (taxData && taxData.success) {
      if (taxData.tax_name !== undefined && taxData.tax_name !== null) {
        setTaxName(String(taxData.tax_name));
      } else if (taxData.tax1_name !== undefined && taxData.tax1_name !== null) {
        setTaxName(String(taxData.tax1_name));
      } else {
        setTaxName('VAT');
      }
      
      if (taxData.tax_per !== undefined && taxData.tax_per !== null) {
        setTaxPercentage(String(taxData.tax_per));
      } else if (taxData.tax1_per !== undefined && taxData.tax1_per !== null) {
        setTaxPercentage(String(taxData.tax1_per));
      } else {
        setTaxPercentage('');
      }
      
      if (taxData.currency !== undefined && taxData.currency !== null) {
        setCurrency(String(taxData.currency));
      }
      
      setHasTaxChanges(false);
    }
  }, [taxData]);

  // Load initial return limit data
  useEffect(() => {
    console.log('=== limitData received ===', limitData);
    
    if (limitData && limitData.success) {
      if (limitData.return_limit !== undefined && limitData.return_limit !== null) {
        setReturnLimit(String(limitData.return_limit));
      } else if (limitData.global_return_limit !== undefined && limitData.global_return_limit !== null) {
        setReturnLimit(String(limitData.global_return_limit));
      } else {
        setReturnLimit('3');
      }
      setHasReturnLimitChanges(false);
    }
  }, [limitData]);

  // Update hasTaxChanges when any tax field changes
  useEffect(() => {
    if (taxData && taxData.success) {
      const originalName = taxData.tax_name || taxData.tax1_name || 'VAT';
      const originalPercentage = taxData.tax_per || taxData.tax1_per || '';
      
      setHasTaxChanges(
        originalName !== taxName ||
        String(originalPercentage) !== taxPercentage ||
        (taxData.currency || 'AED') !== currency
      );
    }
  }, [taxName, taxPercentage, currency, taxData]);

  // Update hasReturnLimitChanges when return limit changes
  useEffect(() => {
    if (limitData && limitData.success) {
      const originalValue = limitData.return_limit || limitData.global_return_limit || '3';
      setHasReturnLimitChanges(String(originalValue) !== returnLimit);
    }
  }, [returnLimit, limitData]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Tax handlers
  const handleTaxNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setTaxName(value);
  };

  const handleTaxPercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '' || /^\d+(\.\d{1,2})?$/.test(value)) {
      setTaxPercentage(value);
    }
  };

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCurrency(value);
  };

  const handleResetTax = () => {
    if (taxData && taxData.success) {
      if (taxData.tax_name !== undefined && taxData.tax_name !== null) {
        setTaxName(String(taxData.tax_name));
      } else if (taxData.tax1_name !== undefined && taxData.tax1_name !== null) {
        setTaxName(String(taxData.tax1_name));
      } else {
        setTaxName('VAT');
      }
      
      if (taxData.tax_per !== undefined && taxData.tax_per !== null) {
        setTaxPercentage(String(taxData.tax_per));
      } else if (taxData.tax1_per !== undefined && taxData.tax1_per !== null) {
        setTaxPercentage(String(taxData.tax1_per));
      } else {
        setTaxPercentage('');
      }
      
      if (taxData.currency !== undefined && taxData.currency !== null) {
        setCurrency(String(taxData.currency));
      }
      setHasTaxChanges(false);
    }
  };

  const handleSaveTax = async () => {
    if (!taxName.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a tax name',
        severity: 'error'
      });
      return;
    }

    if (!taxPercentage || parseFloat(taxPercentage) < 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid tax percentage',
        severity: 'error'
      });
      return;
    }

    try {
      const result = await setProductTax({
        tax_name: taxName,
        tax_per: taxPercentage,
        currency: currency
      }).unwrap();

      console.log('Save tax result:', result);

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || 'Tax settings updated successfully for all products!',
          severity: 'success'
        });
        setHasTaxChanges(false);
        
        await Promise.all([refetchTax(), refetchList()]);
      } else {
        throw new Error(result.message || 'Failed to update tax settings');
      }
    } catch (err: any) {
      console.error('Save tax error:', err);
      let errorMessage = 'Error updating tax settings.';
      
      if (err?.message) {
        errorMessage = err.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // Return limit handlers
  const handleReturnLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setReturnLimit(value);
    }
  };

  const handleResetReturnLimit = () => {
    if (limitData && limitData.success) {
      if (limitData.return_limit !== undefined && limitData.return_limit !== null) {
        setReturnLimit(String(limitData.return_limit));
      } else if (limitData.global_return_limit !== undefined && limitData.global_return_limit !== null) {
        setReturnLimit(String(limitData.global_return_limit));
      } else {
        setReturnLimit('3');
      }
      setHasReturnLimitChanges(false);
    }
  };

  const handleSaveReturnLimit = async () => {
    if (!returnLimit || parseInt(returnLimit) < 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid return limit',
        severity: 'error'
      });
      return;
    }

    try {
      const result = await setProductReturnLimit({
        return_limit: returnLimit
      }).unwrap();

      console.log('Save return limit result:', result);

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || 'Product return limit updated successfully!',
          severity: 'success'
        });
        setHasReturnLimitChanges(false);
        
        await refetchLimit();
      } else {
        throw new Error(result.message || 'Failed to update return limit');
      }
    } catch (err: any) {
      console.error('Save return limit error:', err);
      let errorMessage = 'Error updating product return limit.';
      
      if (err?.message) {
        errorMessage = err.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // Product tax list handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = async () => {
    try {
      await Promise.all([refetchTax(), refetchLimit(), refetchList()]);
      setSnackbar({
        open: true,
        message: 'Settings refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Refresh error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to refresh settings',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isLoading = isTaxLoading || isLimitLoading || isListLoading;
  const isError = isTaxError || isLimitError || isListError;

  // Prepare product tax list data for table
  const productTaxList = productTaxListData?.product_tax_list || [];
  const paginatedList = productTaxList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon sx={{ color: '#202223', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Global Settings
                  </Typography>
                </Box>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#c9cccf',
                    color: '#202223',
                    borderRadius: '8px',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#202223',
                      bgcolor: 'transparent',
                    }
                  }}
                >
                  Refresh
                </Button>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#202223',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label="Tax Settings" />
                    <Tab label="Product Tax List" />
                    <Tab label="Return Settings" />
                  </Tabs>
                </Box>

                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">
                      Failed to load settings. Please try again.
                    </Alert>
                  </Box>
                )}

                {!isLoading && !isError && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Stack spacing={3}>
                      {/* Tax Settings Section - Tab 0 */}
                      {tabValue === 0 && (
                        <Card sx={{ 
                          borderRadius: '12px', 
                          border: '1px solid #e1e3e5',
                          boxShadow: 'none'
                        }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <AttachMoney sx={{ color: '#008060', fontSize: 24 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                                Product Tax Settings
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              Set the default tax name and percentage that will be applied to all products. 
                              Individual products can override these settings.
                            </Typography>

                            {taxData && taxData.success && (
                              <Box sx={{ 
                                bgcolor: '#f6f6f7', 
                                p: 2, 
                                borderRadius: '8px',
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Info sx={{ color: '#2196f3', fontSize: 20 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Current tax: <strong>
                                    {taxData.tax_name || taxData.tax1_name || 'VAT'} 
                                    {(taxData.tax_per || taxData.tax1_per) ? ` (${taxData.tax_per || taxData.tax1_per}%)` : ' (Not set)'}
                                  </strong>
                                  {taxData.currency && ` in ${taxData.currency}`}
                                </Typography>
                              </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Stack spacing={3} sx={{ maxWidth: 500 }}>
                              <TextField
                                label="Tax Name"
                                value={taxName}
                                onChange={handleTaxNameChange}
                                fullWidth
                                placeholder="e.g., VAT, GST, Sales Tax"
                                helperText="Enter the name of the tax"
                                disabled={isTaxLoading}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                  }
                                }}
                              />

                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                  label="Tax Percentage"
                                  value={taxPercentage}
                                  onChange={handleTaxPercentageChange}
                                  fullWidth
                                  type="text"
                                  placeholder="e.g., 5, 18, 20"
                                  helperText="Enter the tax percentage"
                                  disabled={isTaxLoading}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">%</InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                    }
                                  }}
                                />

                                <TextField
                                  label="Currency"
                                  value={currency}
                                  onChange={handleCurrencyChange}
                                  select
                                  fullWidth
                                  helperText="Select currency"
                                  disabled={isTaxLoading}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                    }
                                  }}
                                  SelectProps={{
                                    native: true,
                                  }}
                                >
                                  <option value="AED">AED (د.إ)</option>
                                  <option value="USD">USD ($)</option>
                                  <option value="EUR">EUR (€)</option>
                                  <option value="GBP">GBP (£)</option>
                                  <option value="INR">INR (₹)</option>
                                </TextField>
                              </Box>
                            </Stack>

                            <Box sx={{ 
                              display: 'flex', 
                              gap: 2, 
                              mt: 3,
                              justifyContent: 'flex-end'
                            }}>
                              <Button
                                onClick={handleResetTax}
                                disabled={!hasTaxChanges || isTaxSaving}
                                sx={{ 
                                  textTransform: 'none', 
                                  fontWeight: 600,
                                  color: '#202223',
                                  borderRadius: '8px',
                                  px: 2.5
                                }}
                              >
                                Reset
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={isTaxSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                onClick={handleSaveTax}
                                disabled={!hasTaxChanges || isTaxSaving || !taxName || !taxPercentage}
                                sx={{
                                  bgcolor: '#008060',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  px: 2.5,
                                  '&:hover': { bgcolor: '#006e52' },
                                  '&.Mui-disabled': {
                                    bgcolor: '#c9cccf',
                                    color: '#8c9196'
                                  }
                                }}
                              >
                                {isTaxSaving ? 'Saving...' : 'Save Tax Settings'}
                              </Button>
                            </Box>
                          </Box>
                        </Card>
                      )}

                      {/* Product Tax List Section - Tab 1 */}
                      {tabValue === 1 && (
                        <Card sx={{ 
                          borderRadius: '12px', 
                          border: '1px solid #e1e3e5',
                          boxShadow: 'none'
                        }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <ListIcon sx={{ color: '#008060', fontSize: 24 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                                Product Tax List
                              </Typography>
                              {productTaxListData && productTaxListData.success && (
                                <Chip 
                                  label={`${productTaxListData.count || 0} products`}
                                  size="small"
                                  sx={{ ml: 2, bgcolor: '#e3f2fd', color: '#1976d2' }}
                                />
                              )}
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              View all products and their individual tax settings. Products can have different tax rates than the global default.
                            </Typography>

                            {productTaxList.length === 0 ? (
                              <Box sx={{ 
                                textAlign: 'center', 
                                py: 8, 
                                bgcolor: '#fafafa', 
                                borderRadius: '8px',
                                border: '1px dashed #e1e3e5'
                              }}>
                                <Info sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                  No Product Tax Data
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Product tax information will appear here once available.
                                </Typography>
                              </Box>
                            ) : (
                              <>
                                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e1e3e5' }}>
                                  <Table>
                                    <TableHead sx={{ bgcolor: '#f6f6f7' }}>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Product ID</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Tax Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Tax %</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {paginatedList.map((product) => (
                                        <TableRow key={product.product_id} hover>
                                          <TableCell>{product.product_id}</TableCell>
                                          <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                              {product.proj_name}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={product.tax_name}
                                              size="small"
                                              sx={{ 
                                                bgcolor: '#e8f5e9', 
                                                color: '#2e7d32',
                                                fontWeight: 500
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                              {product.tax_per}%
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={product.currency}
                                              size="small"
                                              variant="outlined"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              <Tooltip title="View Details">
                                                <IconButton size="small">
                                                  <Visibility fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Edit Tax">
                                                <IconButton size="small" color="primary">
                                                  <Edit fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                                
                                <TablePagination
                                  rowsPerPageOptions={[5, 10, 25]}
                                  component="div"
                                  count={productTaxList.length}
                                  rowsPerPage={rowsPerPage}
                                  page={page}
                                  onPageChange={handleChangePage}
                                  onRowsPerPageChange={handleChangeRowsPerPage}
                                  sx={{ mt: 2 }}
                                />
                              </>
                            )}
                          </Box>
                        </Card>
                      )}

                      {/* Product Return Limit Section - Tab 2 */}
                      {tabValue === 2 && (
                        <Card sx={{ 
                          borderRadius: '12px', 
                          border: '1px solid #e1e3e5',
                          boxShadow: 'none'
                        }}>
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <AssignmentReturn sx={{ color: '#008060', fontSize: 24 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                                Product Return Limit
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              Set the maximum number of days within which customers can return products after purchase.
                            </Typography>

                            {limitData && limitData.success && (
                              <Box sx={{ 
                                bgcolor: '#f6f6f7', 
                                p: 2, 
                                borderRadius: '8px',
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Info sx={{ color: '#2196f3', fontSize: 20 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Current return limit: <strong>
                                    {returnLimit ? `${returnLimit} days` : 'Not set'}
                                  </strong>
                                </Typography>
                              </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ maxWidth: 250 }}>
                              <TextField
                                label="Return Limit (Days)"
                                value={returnLimit}
                                onChange={handleReturnLimitChange}
                                fullWidth
                                type="text"
                                placeholder="e.g., 7"
                                helperText="Enter the number of days customers have to return products"
                                disabled={isLimitLoading}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      days
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                  }
                                }}
                              />
                            </Box>

                            <Box sx={{ 
                              display: 'flex', 
                              gap: 2, 
                              mt: 3,
                              justifyContent: 'flex-end'
                            }}>
                              <Button
                                onClick={handleResetReturnLimit}
                                disabled={!hasReturnLimitChanges || isLimitSaving}
                                sx={{ 
                                  textTransform: 'none', 
                                  fontWeight: 600,
                                  color: '#202223',
                                  borderRadius: '8px',
                                  px: 2.5
                                }}
                              >
                                Reset
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={isLimitSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                onClick={handleSaveReturnLimit}
                                disabled={!hasReturnLimitChanges || isLimitSaving || !returnLimit}
                                sx={{
                                  bgcolor: '#008060',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  px: 2.5,
                                  '&:hover': { bgcolor: '#006e52' },
                                  '&.Mui-disabled': {
                                    bgcolor: '#c9cccf',
                                    color: '#8c9196'
                                  }
                                }}
                              >
                                {isLimitSaving ? 'Saving...' : 'Save Changes'}
                              </Button>
                            </Box>
                          </Box>
                        </Card>
                      )}
                    </Stack>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%', 
            '&.MuiAlert-filledSuccess': {
              bgcolor: '#0f702aff',
            },
            '&.MuiAlert-filledError': {
              bgcolor: '#0f702aff',
            }
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GlobalSettings;